import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Bookmark, Loader2, EyeOff } from 'lucide-react';
import { DealMiniTable } from '../components/DealMiniTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { STAGE_LABELS, STAGE_ORDER } from '../lib/stages';

export default function BookmarksPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hidePassed, setHidePassed] = useState(false);
  const [stageFilter, setStageFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getBookmarkDeals();
      setDeals(res.data || []);
    } catch { toast.error('Failed to load bookmarks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const removeBookmark = async (id) => {
    try {
      await api.toggleBookmark(id);
      setDeals(prev => prev.filter(d => d.id !== id));
      toast.success('Bookmark removed');
    } catch { toast.error('Failed to remove bookmark'); }
  };

  // Stages actually present in the user's bookmarks, in pipeline order.
  const stageOptions = useMemo(() => {
    const present = new Set(deals.map(d => d.pipeline_stage).filter(Boolean));
    return STAGE_ORDER.filter(s => present.has(s));
  }, [deals]);

  const filtered = useMemo(() => deals.filter(d => {
    if (hidePassed && d.pipeline_stage === 'passed') return false;
    if (stageFilter !== 'all' && d.pipeline_stage !== stageFilter) return false;
    return true;
  }), [deals, hidePassed, stageFilter]);

  const passedCount = useMemo(() => deals.filter(d => d.pipeline_stage === 'passed').length, [deals]);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="bookmarks-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
            <Bookmark className="w-6 h-6 text-blue-400" fill="currentColor" /> My Bookmarks
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">Your private saved deals — only visible to you</p>
        </div>

        {deals.length > 0 && (
          <div className="flex items-center gap-2">
            <button onClick={() => setHidePassed(v => !v)} data-testid="bookmarks-hide-passed-toggle"
              className={`text-xs h-9 px-2.5 rounded-sm border transition-colors inline-flex items-center gap-1.5 ${hidePassed ? 'bg-red-500/15 border-red-500/40 text-red-400' : 'border-border text-muted-foreground hover:text-foreground'}`}>
              <EyeOff className="w-3.5 h-3.5" />
              {hidePassed ? 'Passed hidden' : 'Hide passed'}
              {passedCount > 0 && <span className="text-[10px] font-mono opacity-70">({passedCount})</span>}
            </button>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger data-testid="bookmarks-stage-filter"
                className="w-44 bg-secondary border-border rounded-sm h-9 text-xs text-foreground">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm max-h-[400px]">
                <SelectItem value="all" className="text-xs">All Stages</SelectItem>
                {stageOptions.map(s => (
                  <SelectItem key={s} value={s} className="text-xs">{STAGE_LABELS[s] || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>
      ) : deals.length === 0 ? (
        <div className="text-center py-24 border border-border rounded-sm bg-card" data-testid="bookmarks-empty">
          <Bookmark className="w-10 h-10 text-muted-foreground/40 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-foreground text-sm font-medium mb-1">No bookmarks yet</p>
          <p className="text-muted-foreground/60 text-xs">Click the bookmark icon on any deal to privately save it here.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-border rounded-sm bg-card" data-testid="bookmarks-no-match">
          <p className="text-foreground text-sm font-medium mb-1">No bookmarks match your filters</p>
          <p className="text-muted-foreground/60 text-xs">Adjust the stage filter or show passed deals again.</p>
        </div>
      ) : (
        <DealMiniTable deals={filtered} type="bookmark" onRemove={removeBookmark} />
      )}
    </div>
  );
}
